import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, FileSpreadsheet, CheckCircle, AlertCircle, ArrowLeft, Link as LinkIcon, RefreshCw, Loader2, Settings, Wand2, ChevronDown, ChevronUp, Image, Trash2, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import api from '@/lib/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Compact field mapper for inline mapping step
const QuickFieldMapper = ({ label, required, fieldKey, mapping, updateMapping, xmlFields, samples }) => (
  <div className="space-y-1">
    <Label className="text-xs font-medium flex items-center gap-1">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Select
      value={mapping[fieldKey] || ''}
      onValueChange={(v) => updateMapping(fieldKey, v === '__none__' ? '' : v)}
    >
      <SelectTrigger className="h-8 text-xs" data-testid={`auto-map-${fieldKey}`}>
        <SelectValue placeholder="Seçin..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">-- Yok --</SelectItem>
        {xmlFields.map((f) => (
          <SelectItem key={f} value={f}>
            <span className="flex items-center gap-1.5">
              {f}
              {samples[f] && (
                <span className="text-[10px] text-gray-400 truncate max-w-[140px]">
                  ({String(samples[f]).substring(0, 30)})
                </span>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {mapping[fieldKey] && samples[mapping[fieldKey]] && (
      <p className="text-[10px] text-gray-400 truncate">
        {String(samples[mapping[fieldKey]]).substring(0, 60)}
      </p>
    )}
  </div>
);

const SellerBulkUploadPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const spreadsheetInputRef = useRef(null);

  // Core state
  const [activeTab, setActiveTab] = useState('url');
  const [file, setFile] = useState(null);
  const [spreadsheetFile, setSpreadsheetFile] = useState(null);
  const [xmlUrl, setXmlUrl] = useState('');
  const [extraXmlUrls, setExtraXmlUrls] = useState('');
  const [imageStoreRoot, setImageStoreRoot] = useState('');
  const [dragActive, setDragActive] = useState(false);

  // Loading states
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);

  // Mapping state
  const [existingMapping, setExistingMapping] = useState(null);
  const [mappingLoaded, setMappingLoaded] = useState(false);
  const [showMappingStep, setShowMappingStep] = useState(false);
  const [mapping, setMapping] = useState({});
  const [xmlFields, setXmlFields] = useState([]);
  const [fieldSamples, setFieldSamples] = useState({});
  const [analysisInfo, setAnalysisInfo] = useState(null);
  const [detectedVariants, setDetectedVariants] = useState(null);
  const [sectionsOpen, setSectionsOpen] = useState({ basic: true, images: false, variants: false });

  // Import progress
  const [importProgress, setImportProgress] = useState(null);
  const [rematching, setRematching] = useState(false);

  // Edit mapping state
  const [editingMapping, setEditingMapping] = useState(false);

  // Load existing mapping on mount
  useEffect(() => {
    loadExistingMapping();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadExistingMapping = async () => {
    try {
      const res = await api.get('/seller/xml-mapping');
      if (res.data.success && res.data.data) {
        setExistingMapping(res.data.data);
      }
    } catch { /* no mapping yet */ }
    setMappingLoaded(true);
  };

  const parseExtraXmlUrls = (text) =>
    String(text || '')
      .split('\n')
      .map((s) => s.trim())
      .filter((u) => u.startsWith('http://') || u.startsWith('https://'));

  useEffect(() => {
    if (!existingMapping) {
      setExtraXmlUrls('');
      return;
    }
    const u = existingMapping.xml_url || '';
    if (u.startsWith('http://') || u.startsWith('https://')) {
      setXmlUrl(u);
    }
    setExtraXmlUrls((existingMapping.xml_feed_urls || []).join('\n'));
  }, [existingMapping]);

  useEffect(() => {
    if (!existingMapping || !mappingLoaded) return;
    if (activeTab === 'spreadsheet') {
      setMapping(existingMapping.tabular_mapping || {});
    } else {
      setMapping(existingMapping.mapping || {});
    }
  }, [activeTab, existingMapping, mappingLoaded]);

  // ---- Drag & Drop ----
  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (activeTab === 'spreadsheet') {
      if (f.name.match(/\.(csv|xlsx|xls)$/i)) setSpreadsheetFile(f);
      else toast.error('Excel / CSV sekmesinde .csv, .xlsx veya .xls yükleyin');
    } else if (f.name.match(/\.(xml|csv)$/i)) setFile(f);
    else toast.error('XML veya CSV dosyası seçin');
  };
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (activeTab === 'spreadsheet') {
      if (f.name.match(/\.(csv|xlsx|xls)$/i)) setSpreadsheetFile(f);
      else toast.error('.csv, .xlsx veya .xls seçin');
    } else if (f.name.match(/\.(xml|csv)$/i)) setFile(f);
    else toast.error('Sadece XML ve CSV dosyaları destekleniyor');
  };

  const handleSpreadsheetFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f?.name.match(/\.(csv|xlsx|xls)$/i)) setSpreadsheetFile(f);
    else toast.error('.csv, .xlsx veya .xls seçin');
    e.target.value = '';
  };

  const hasSavedXmlMapping = !!(
    existingMapping?.mapping?.name &&
    existingMapping?.mapping?.price &&
    existingMapping?.mapping?.stock
  );
  const hasSavedTabularMapping = !!(
    existingMapping?.tabular_mapping?.name &&
    existingMapping?.tabular_mapping?.price &&
    existingMapping?.tabular_mapping?.stock
  );

  // ---- Main Import Flow ----
  const handleImport = async (source) => {
    if (source === 'spreadsheet') {
      if (!spreadsheetFile) { toast.error('Lütfen Excel veya CSV dosyası seçin'); return; }
      if (hasSavedTabularMapping) {
        await doImport('spreadsheet');
        return;
      }
      await analyzeTabularAndSuggest();
      return;
    }
    if (source === 'file' && !file) { toast.error('Lütfen dosya seçin'); return; }
    if (source === 'url' && !xmlUrl) { toast.error('Lütfen XML URL girin'); return; }

    if (hasSavedXmlMapping) {
      await doImport(source);
      return;
    }

    await analyzeAndSuggest(source);
  };

  const analyzeAndSuggest = async (source) => {
    try {
      setAnalyzing(true);
      setShowMappingStep(false);

      let analysisData;
      if (source === 'file') {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/seller/xml-mapping/analyze-xml-file', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        analysisData = res.data.data;
      } else {
        const res = await api.post('/seller/xml-mapping/analyze-xml', { xml_url: xmlUrl });
        analysisData = res.data.data;
      }

      setXmlFields(analysisData.available_fields || []);
      setFieldSamples(analysisData.field_samples || {});
      setAnalysisInfo({ totalProducts: analysisData.total_products });

      // Now get auto-suggestions
      const suggestRes = await api.post('/seller/xml-mapping/auto-suggest', {
        available_fields: analysisData.available_fields,
        field_samples: analysisData.field_samples
      });

      if (suggestRes.data.success) {
        setMapping(suggestRes.data.data.suggestions);
        setDetectedVariants(suggestRes.data.data.detected_variants);
        toast.success(`${analysisData.total_products} ürün bulundu, ${suggestRes.data.data.matched_count} alan otomatik eşleştirildi`);
      }

      setShowMappingStep(true);
    } catch (error) {
      const detail = error.response?.data?.detail || 'XML analizi başarısız';
      toast.error(detail);
    } finally {
      setAnalyzing(false);
    }
  };

  const analyzeTabularAndSuggest = async () => {
    if (!spreadsheetFile) {
      toast.error('Önce dosya seçin');
      return;
    }
    try {
      setAnalyzing(true);
      setShowMappingStep(false);

      const formData = new FormData();
      formData.append('file', spreadsheetFile);
      const res = await api.post('/seller/xml-mapping/analyze-tabular-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const analysisData = res.data.data;
      setXmlFields(analysisData.available_fields || []);
      setFieldSamples(analysisData.field_samples || {});
      setAnalysisInfo({ totalProducts: analysisData.total_products });

      const suggestRes = await api.post('/seller/xml-mapping/auto-suggest', {
        available_fields: analysisData.available_fields,
        field_samples: analysisData.field_samples,
      });

      if (suggestRes.data.success) {
        setMapping(suggestRes.data.data.suggestions);
        setDetectedVariants(suggestRes.data.data.detected_variants);
        toast.success(
          `${analysisData.total_products} satır bulundu, ${suggestRes.data.data.matched_count} alan otomatik eşleştirildi`,
        );
      }

      setShowMappingStep(true);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Dosya analizi başarısız';
      toast.error(detail);
    } finally {
      setAnalyzing(false);
    }
  };

  const confirmMappingAndImport = async () => {
    // Validate required fields
    if (!mapping.name || !mapping.price || !mapping.stock) {
      toast.error('Zorunlu alanları eşleştirin: Ürün Adı, Fiyat, Stok');
      return;
    }

    try {
      if (activeTab === 'spreadsheet') {
        await api.post('/seller/xml-mapping', {
          tabular_mapping: mapping,
          is_active: true,
        });
        setExistingMapping((prev) => ({ ...(prev || {}), tabular_mapping: mapping }));
        toast.success('Excel/CSV sütun eşlemesi kaydedildi!');
        await doImport('spreadsheet');
      } else {
        await api.post('/seller/xml-mapping', {
          xml_url: xmlUrl || `file://${file?.name || 'upload'}`,
          xml_feed_urls: parseExtraXmlUrls(extraXmlUrls),
          mapping: mapping,
          is_active: true
        });
        setExistingMapping((prev) => ({
          ...(prev || {}),
          mapping,
          xml_url: xmlUrl || prev?.xml_url,
          xml_feed_urls: parseExtraXmlUrls(extraXmlUrls),
        }));
        toast.success('Mapping kaydedildi!');
        const source = activeTab === 'url' ? 'url' : 'file';
        await doImport(source);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mapping kaydedilemedi');
    }
  };

  const doImport = async (source) => {
    try {
      setImporting(true);
      setShowMappingStep(false);
      setImportProgress({ status: 'importing', message: 'Ürünler işleniyor...', percent: 30, total: 0, processed: 0 });

      let data;
      if (source === 'spreadsheet') {
        const formData = new FormData();
        formData.append('file', spreadsheetFile);
        if (imageStoreRoot.trim()) {
          formData.append('store_url', imageStoreRoot.trim());
        }
        const token = localStorage.getItem('token');

        data = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.timeout = 300000;
          xhr.open('POST', `${API}/api/seller/products/bulk-upload`);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const pct = Math.round((e.loaded / e.total) * 50);
              setImportProgress(p => ({ ...p, percent: pct, message: `Dosya yükleniyor... %${pct * 2}` }));
            }
          };
          xhr.onload = () => {
            try {
              const result = JSON.parse(xhr.responseText);
              xhr.status >= 200 && xhr.status < 300 ? resolve(result) : reject(result);
            } catch { reject({ detail: 'Sunucu yanıtı okunamadı.' }); }
          };
          xhr.onerror = () => reject({ detail: 'Ağ hatası - bağlantı kesildi' });
          xhr.ontimeout = () => reject({ detail: 'İşlem zaman aşımına uğradı.' });
          xhr.send(formData);
        });
      } else if (source === 'file') {
        const formData = new FormData();
        formData.append('file', file);
        if (imageStoreRoot.trim()) {
          formData.append('store_url', imageStoreRoot.trim());
        }
        const token = localStorage.getItem('token');

        data = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.timeout = 300000; // 5 minutes for large files
          xhr.open('POST', `${API}/api/seller/products/bulk-upload`);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const pct = Math.round((e.loaded / e.total) * 50);
              setImportProgress(p => ({ ...p, percent: pct, message: `Dosya yükleniyor... %${pct * 2}` }));
            }
          };
          xhr.onload = () => {
            try {
              const result = JSON.parse(xhr.responseText);
              xhr.status >= 200 && xhr.status < 300 ? resolve(result) : reject(result);
            } catch { reject({ detail: 'Sunucu yanıtı okunamadı. Import arka planda devam ediyor olabilir.' }); }
          };
          xhr.onerror = () => reject({ detail: 'Ağ hatası - bağlantı kesildi' });
          xhr.ontimeout = () => reject({ detail: 'İşlem zaman aşımına uğradı. Büyük dosyalar için lütfen birkaç dakika bekleyin.' });
          xhr.send(formData);
        });
      } else {
        // URL import - backend returns immediately with import_id, then we poll for progress
        try {
          const res = await api.post('/seller/products/import-from-url', { url: xmlUrl });
          data = res.data;
          const importId = data.import_id;
          
          if (importId) {
            // Poll for progress
            setImportProgress({
              status: 'importing', percent: 5,
              message: 'XML dosyasi indiriliyor ve isleniyor...',
              total: 0, processed: 0, created: 0, updated: 0
            });
            
            let completed = false;
            while (!completed) {
              await new Promise(r => setTimeout(r, 2000));
              try {
                const progressRes = await api.get(`/seller/products/import-progress/${importId}`);
                const p = progressRes.data;
                setImportProgress({
                  status: p.status, percent: p.percent || 0,
                  message: p.message || 'Isleniyor...',
                  total: p.total || 0, processed: p.processed || 0,
                  created: p.created || 0, updated: p.updated || 0
                });
                if (p.status === 'completed') {
                  data = { count: p.count || p.processed, new_count: p.created || p.new_count, updated_count: p.updated || p.updated_count, message: p.message };
                  completed = true;
                } else if (p.status === 'error') {
                  throw { detail: p.message || 'Import basarisiz' };
                }
              } catch (pollErr) {
                if (pollErr?.detail) throw pollErr;
                // Ignore transient poll errors, keep trying
              }
            }
          }
        } catch (fetchErr) {
          throw fetchErr;
        }
      }

      setImportProgress({
        status: 'completed', percent: 100,
        message: data.message || `${data.count} ürün işlendi`,
        total: data.count || 0, processed: data.count || 0,
        created: data.new_count || 0, updated: data.updated_count || 0
      });
      toast.success(`${data.count} ürün başarıyla yüklendi!`);
      setFile(null);
      setSpreadsheetFile(null);
    } catch (error) {
      const msg = error?.detail || error?.message || 'İçe aktarma başarısız';
      setImportProgress({ status: 'error', percent: 0, message: msg });
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const updateMapping = (key, value) => {
    setMapping(prev => ({ ...prev, [key]: value || undefined }));
  };

  const deleteMapping = async () => {
    try {
      await api.delete('/seller/xml-mapping');
      setExistingMapping(null);
      setMapping({});
      setDetectedVariants(null);
      setEditingMapping(false);
      toast.success('Mapping silindi. Sonraki yüklemede yeni eşleştirme yapabilirsiniz.');
    } catch {
      toast.error('Mapping silinemedi');
    }
  };

  const editCurrentMapping = () => {
    if (activeTab === 'spreadsheet') {
      const tm = existingMapping?.tabular_mapping || {};
      setMapping({ ...tm });
      const cols = [...new Set(Object.values(tm).filter(Boolean))];
      setXmlFields(cols.length ? cols : []);
      setFieldSamples({});
    } else if (existingMapping?.mapping) {
      setMapping(existingMapping.mapping);
      const existingFields = Object.values(existingMapping.mapping).filter(Boolean);
      if (existingFields.length > 0 && xmlFields.length === 0) {
        setXmlFields(existingFields);
      }
    }
    setEditingMapping(true);
    setShowMappingStep(true);
    setSectionsOpen({ basic: true, images: false, variants: false });
  };

  const saveMappingEdit = async () => {
    if (!mapping.name || !mapping.price || !mapping.stock) {
      toast.error('Zorunlu alanları eşleştirin: Ürün Adı, Fiyat, Stok');
      return;
    }
    try {
      if (activeTab === 'spreadsheet') {
        await api.post('/seller/xml-mapping', {
          tabular_mapping: mapping,
          is_active: true,
        });
        setExistingMapping((prev) => ({ ...(prev || {}), tabular_mapping: mapping }));
      } else {
        await api.post('/seller/xml-mapping', {
          xml_url: (xmlUrl && xmlUrl.startsWith('http')) ? xmlUrl : (existingMapping?.xml_url || 'manual'),
          xml_feed_urls: parseExtraXmlUrls(extraXmlUrls),
          mapping: mapping,
          is_active: true
        });
        setExistingMapping((prev) => ({
          ...(prev || {}),
          mapping,
          xml_url: (xmlUrl && xmlUrl.startsWith('http')) ? xmlUrl : prev?.xml_url,
          xml_feed_urls: parseExtraXmlUrls(extraXmlUrls),
        }));
      }
      setShowMappingStep(false);
      setEditingMapping(false);
      toast.success('Mapping güncellendi!');
    } catch {
      toast.error('Mapping güncellenemedi');
    }
  };

  const toggleSection = (s) => setSectionsOpen(prev => ({ ...prev, [s]: !prev[s] }));

  const rematchCategoriesFromXml = async (dryRun) => {
    if (!hasSavedXmlMapping) {
      toast.error('Önce XML eşlemesini (özellikle kategori) kaydedin.');
      return;
    }
    const fromForm = [...(xmlUrl.trim() ? [xmlUrl.trim()] : []), ...parseExtraXmlUrls(extraXmlUrls)];
    const uniq = [...new Set(fromForm.filter((u) => u.startsWith('http://') || u.startsWith('https://')))];
    try {
      setRematching(true);
      const body = uniq.length ? { xml_urls: uniq, dry_run: dryRun } : { dry_run: dryRun };
      const res = await api.post('/seller/xml-mapping/rematch-categories', body);
      const d = res.data?.data;
      toast.success(res.data?.message || 'Tamamlandı');
      if (dryRun && d?.sample_updates?.length) {
        toast.info(`Örnek: ${d.sample_updates.slice(0, 3).map((x) => `${x.sku} → ${x.new_category}`).join('; ')}`, { duration: 6000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kategori yenileme başarısız');
    } finally {
      setRematching(false);
    }
  };

  const BASIC_FIELDS = [
    { key: 'name', label: 'Ürün Adı', required: true },
    { key: 'price', label: 'Satış Fiyatı', required: true },
    { key: 'stock', label: 'Stok', required: true },
    { key: 'description', label: 'Açıklama' },
    { key: 'sku', label: 'Stok Kodu (SKU)' },
    { key: 'barcode', label: 'Barkod' },
    { key: 'brand', label: 'Marka' },
    { key: 'category', label: 'Kategori' },
    { key: 'currency', label: 'Para Birimi' },
    { key: 'tax_rate', label: 'KDV Oranı' },
  ];
  const IMAGE_FIELDS = Array.from({ length: 10 }, (_, i) => ({ key: `image_${i + 1}`, label: `Görsel ${i + 1}` }));
  const VARIANT_FIELDS = [
    { key: 'color', label: 'Renk' },
    { key: 'size', label: 'Beden' },
  ];

  const isLoading = analyzing || importing;
  const hasMapping = !!(existingMapping && (hasSavedXmlMapping || hasSavedTabularMapping));

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/seller')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Panele Dön
        </Button>
        <div className="flex items-center gap-2">
          {hasMapping && (
            <>
              <Badge variant="outline" className="text-green-600 border-green-300 text-xs" data-testid="mapping-active-badge">
                <CheckCircle className="w-3 h-3 mr-1" /> Mapping Aktif
              </Badge>
              {hasSavedXmlMapping && (
                <Badge variant="secondary" className="text-xs">XML</Badge>
              )}
              {hasSavedTabularMapping && (
                <Badge variant="secondary" className="text-xs">Excel/CSV</Badge>
              )}
              <Button variant="outline" size="sm" onClick={editCurrentMapping} data-testid="edit-mapping-btn">
                <Edit3 className="mr-1.5 h-3.5 w-3.5" />
                Düzenle
              </Button>
              <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={deleteMapping} data-testid="delete-mapping-btn">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Sil
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Upload Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Toplu Ürün Yükleme
          </CardTitle>
          <CardDescription>
            {activeTab === 'spreadsheet'
              ? (hasSavedTabularMapping
                  ? 'Kayıtlı sütun eşlemesi ile Excel/CSV içe aktarılır'
                  : 'Önce dosyayı analiz edin; sütunları eşleştirip kaydedin')
              : (hasSavedXmlMapping
                  ? 'Kayıtlı XML mapping ile otomatik içe aktarım yapılacak'
                  : 'XML yükleyin veya link verin; sistem alan eşleştirmesi önerecek')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="url" className="flex items-center gap-2" disabled={isLoading}>
                <LinkIcon className="h-4 w-4" /> XML Link
              </TabsTrigger>
              <TabsTrigger value="file" className="flex items-center gap-2" disabled={isLoading}>
                <FileText className="h-4 w-4" /> XML Dosya
              </TabsTrigger>
              <TabsTrigger value="spreadsheet" className="flex items-center gap-2" disabled={isLoading}>
                <FileSpreadsheet className="h-4 w-4" /> Excel / CSV
              </TabsTrigger>
            </TabsList>

            {(activeTab === 'file' || activeTab === 'spreadsheet') && (
              <div className="space-y-2 mb-4">
                <Label htmlFor="image-store-root">Mağaza kök URL (görseller göreliyse)</Label>
                <Input
                  id="image-store-root"
                  type="url"
                  placeholder="https://www.eminonutoptanticaret.com"
                  value={imageStoreRoot}
                  onChange={(e) => setImageStoreRoot(e.target.value)}
                  disabled={isLoading}
                  data-testid="image-store-root-input"
                />
                <p className="text-xs text-muted-foreground">
                  Örn. OpenCart akışında <code className="bg-muted px-1 rounded text-[11px]">/image/cache/...</code> veya{' '}
                  <code className="bg-muted px-1 rounded text-[11px]">catalog/product/...</code> ise buraya ana site adresini yazın —
                  böylece linkler tam https olur ve vitrin kırığı azalır.
                </p>
              </div>
            )}

            {/* URL Tab */}
            <TabsContent value="url" className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="url" placeholder="https://example.com/products.xml"
                  value={xmlUrl} onChange={(e) => setXmlUrl(e.target.value)}
                  disabled={isLoading} data-testid="xml-url-input"
                />
                <Button
                  onClick={() => handleImport('url')}
                  disabled={isLoading || !xmlUrl}
                  className="min-w-[140px]"
                  data-testid="import-url-btn"
                >
                  {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  {analyzing ? 'Analiz...' : hasSavedXmlMapping ? 'İçe Aktar' : 'Analiz & İçe Aktar'}
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="extra-xml-urls">Ek XML akış linkleri (isteğe bağlı — satır başına bir URL)</Label>
                <Textarea
                  id="extra-xml-urls"
                  placeholder={'https://tedarikci.com/feed2.xml\nhttps://tedarikci.com/feed3.xml'}
                  value={extraXmlUrls}
                  onChange={(e) => setExtraXmlUrls(e.target.value)}
                  disabled={isLoading}
                  rows={3}
                  className="text-sm font-mono"
                  data-testid="extra-xml-urls"
                />
                <p className="text-xs text-muted-foreground">
                  Bu linkler mapping ile birlikte kaydedilir; aynı ürün şablonu için birden fazla kaynak kullanabilirsiniz.
                  / Saved with mapping; multiple feeds with the same product shape.
                </p>
              </div>
              {hasSavedXmlMapping && (existingMapping?.mapping?.category) && (
                <div className="flex flex-wrap gap-2 items-center rounded-lg border bg-muted/30 p-3">
                  <span className="text-sm text-muted-foreground mr-auto">
                    Mağazadaki ürünlerin kategorisini kayıtlı XML’den yeniden oku (SKU / barkod eşleşmesi).
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={rematching || isLoading}
                    onClick={() => rematchCategoriesFromXml(true)}
                    data-testid="rematch-categories-preview"
                  >
                    {rematching ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                    Önizle
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={rematching || isLoading}
                    onClick={() => rematchCategoriesFromXml(false)}
                    data-testid="rematch-categories-apply"
                  >
                    {rematching ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                    Kategorileri uygula
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* File Tab */}
            <TabsContent value="file" className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${dragActive ? 'border-primary bg-primary/5' : 'border-gray-200'}`}
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
              >
                <input type="file" ref={fileInputRef} className="hidden" accept=".xml,.csv" onChange={handleFileChange} />
                {file ? (
                  <div className="space-y-3">
                    <FileText className="h-10 w-10 text-primary mx-auto" />
                    <div>
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <div className="flex justify-center gap-3">
                      <Button variant="outline" size="sm" onClick={() => setFile(null)} disabled={isLoading}>Değiştir</Button>
                      <Button size="sm" onClick={() => handleImport('file')} disabled={isLoading} data-testid="import-file-btn">
                        {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                        {analyzing ? 'Analiz...' : hasSavedXmlMapping ? 'İçe Aktar' : 'Analiz & İçe Aktar'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label onClick={() => fileInputRef.current?.click()} className="cursor-pointer block">
                    <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                    <p className="font-medium">Dosyayı sürükleyip bırakın veya <span className="text-primary">seçin</span></p>
                    <p className="text-sm text-muted-foreground mt-1">XML veya CSV (max 10MB)</p>
                  </label>
                )}
              </div>
            </TabsContent>

            {/* Excel / CSV tab — sütun eşleştirmeli (mapping kaydı) */}
            <TabsContent value="spreadsheet" className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${dragActive ? 'border-primary bg-primary/5' : 'border-gray-200'}`}
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
              >
                <input
                  type="file"
                  ref={spreadsheetInputRef}
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleSpreadsheetFileChange}
                />
                {spreadsheetFile ? (
                  <div className="space-y-3">
                    <FileSpreadsheet className="h-10 w-10 text-primary mx-auto" />
                    <div>
                      <p className="font-medium">{spreadsheetFile.name}</p>
                      <p className="text-sm text-muted-foreground">{(spreadsheetFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <div className="flex justify-center gap-3">
                      <Button variant="outline" size="sm" onClick={() => setSpreadsheetFile(null)} disabled={isLoading}>Değiştir</Button>
                      <Button size="sm" onClick={() => handleImport('spreadsheet')} disabled={isLoading} data-testid="import-spreadsheet-btn">
                        {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                        {analyzing ? 'Analiz...' : hasSavedTabularMapping ? 'İçe Aktar' : 'Analiz & İçe Aktar'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => spreadsheetInputRef.current?.click()} className="cursor-pointer block w-full text-center">
                    <FileSpreadsheet className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                    <p className="font-medium">.xlsx, .xls veya .csv seçin veya sürükleyin</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      İlk yüklemede sütun eşleştirmesi yapılır ve kaydedilir; sonraki yüklemelerde aynı şablon kullanılır.
                    </p>
                  </button>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* ===== INLINE MAPPING STEP ===== */}
          {showMappingStep && !importing && (
            <Card className="mt-6 border-2 border-amber-300 bg-amber-50/50" data-testid="auto-mapping-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wand2 className="h-5 w-5 text-amber-600" />
                  Otomatik Alan Eşleştirme
                  {analysisInfo && (
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {analysisInfo.totalProducts} ürün bulundu
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  {activeTab === 'spreadsheet'
                    ? 'Dosyadaki sütun başlıkları listelenir. Zorunlu alanları kontrol edip onaylayın.'
                    : 'Sistem XML alanlarınızı otomatik eşleştirdi. Kontrol edin, gerekirse düzeltin ve onaylayın.'}
                  <strong className="text-amber-700"> Bu ayar kaydedilecek, sonraki importlarda otomatik kullanılacak.</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Basic Fields - Always Open */}
                <div>
                  <button onClick={() => toggleSection('basic')} className="flex items-center gap-2 w-full text-left mb-2" type="button">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-sm font-semibold text-gray-700">Temel Bilgiler</span>
                    {sectionsOpen.basic ? <ChevronUp className="w-4 h-4 ml-auto text-gray-400" /> : <ChevronDown className="w-4 h-4 ml-auto text-gray-400" />}
                  </button>
                  {sectionsOpen.basic && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {BASIC_FIELDS.map(f => (
                        <QuickFieldMapper key={f.key} fieldKey={f.key} label={f.label} required={f.required}
                          mapping={mapping} updateMapping={updateMapping} xmlFields={xmlFields} samples={fieldSamples} />
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Images */}
                <div>
                  <button onClick={() => toggleSection('images')} className="flex items-center gap-2 w-full text-left mb-2" type="button">
                    <Image className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-semibold text-gray-700">Görseller</span>
                    <Badge variant="outline" className="text-xs ml-1">{IMAGE_FIELDS.filter(f => mapping[f.key]).length} eşleşti</Badge>
                    {sectionsOpen.images ? <ChevronUp className="w-4 h-4 ml-auto text-gray-400" /> : <ChevronDown className="w-4 h-4 ml-auto text-gray-400" />}
                  </button>
                  {sectionsOpen.images && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {IMAGE_FIELDS.map(f => (
                        <QuickFieldMapper key={f.key} fieldKey={f.key} label={f.label}
                          mapping={mapping} updateMapping={updateMapping} xmlFields={xmlFields} samples={fieldSamples} />
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Variants */}
                <div>
                  <button onClick={() => toggleSection('variants')} className="flex items-center gap-2 w-full text-left mb-2" type="button">
                    <Settings className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-semibold text-gray-700">Varyasyonlar</span>
                    {detectedVariants?.auto_extracted && (
                      <Badge className="bg-green-100 text-green-700 border-green-300 text-xs ml-1">
                        <CheckCircle className="w-3 h-3 mr-1" /> Otomatik Tespit Edildi
                      </Badge>
                    )}
                    {sectionsOpen.variants ? <ChevronUp className="w-4 h-4 ml-auto text-gray-400" /> : <ChevronDown className="w-4 h-4 ml-auto text-gray-400" />}
                  </button>
                  {sectionsOpen.variants && (
                    <>
                      {detectedVariants?.auto_extracted ? (
                        <Alert className="mb-3 bg-green-50 border-green-200" data-testid="variants-auto-detected">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <AlertDescription className="text-green-800 text-xs">
                            <strong>Varyantlar otomatik olarak algılandı!</strong> XML'deki <code>&lt;variants&gt;</code> bloğundan renk, beden, stok ve barkod bilgileri otomatik çekilecek.
                            {detectedVariants.attributes?.length > 0 && (
                              <span className="block mt-1">
                                Tespit edilen özellikler:{' '}
                                {detectedVariants.attributes.map((a, i) => (
                                  <Badge key={i} variant="outline" className="text-[10px] mr-1">{a.label}</Badge>
                                ))}
                              </span>
                            )}
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <>
                          <Alert className="mb-3 bg-purple-50 border-purple-200">
                            <AlertDescription className="text-purple-700 text-xs">
                              XML'de ayrı renk/beden alanları varsa burada eşleştirin. Nested <code>&lt;variants&gt;</code> yapısı varsa otomatik algılanır.
                            </AlertDescription>
                          </Alert>
                          <div className="grid grid-cols-2 gap-3">
                            {VARIANT_FIELDS.map(f => (
                              <QuickFieldMapper key={f.key} fieldKey={f.key} label={f.label}
                                mapping={mapping} updateMapping={updateMapping} xmlFields={xmlFields} samples={fieldSamples} />
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Price Warning */}
                {mapping.price && fieldSamples[mapping.price] && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-blue-800 text-xs">
                      <strong>Satış Fiyatı:</strong> &quot;{mapping.price}&quot; alanından alınacak (Örnek değer: {fieldSamples[mapping.price]}).
                      Farklı bir fiyat alanı kullanmak istiyorsanız yukarıdan değiştirebilirsiniz.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>

              <div className="px-6 pb-4 flex justify-between items-center">
                <Button variant="ghost" size="sm" onClick={() => { setShowMappingStep(false); setEditingMapping(false); }}>
                  Vazgeç
                </Button>
                {editingMapping ? (
                  <Button onClick={saveMappingEdit} data-testid="save-mapping-btn"
                    className="bg-blue-600 hover:bg-blue-700">
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Mapping'i Kaydet
                  </Button>
                ) : (
                  <Button onClick={confirmMappingAndImport} data-testid="confirm-mapping-btn"
                    className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Onayla ve İçe Aktar
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* ===== PROGRESS ===== */}
          {importProgress && (
            <Card className="border-2 mt-4" data-testid="import-progress-card">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium flex items-center gap-2">
                    {importProgress.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {importProgress.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500" />}
                    {['uploading', 'importing', 'fetching', 'parsing'].includes(importProgress.status) && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                    {importProgress.status === 'uploading' && 'Dosya Yükleniyor...'}
                    {importProgress.status === 'fetching' && 'XML İndiriliyor...'}
                    {importProgress.status === 'parsing' && 'XML Ayrıştırılıyor...'}
                    {importProgress.status === 'importing' && 'Ürünler İşleniyor...'}
                    {importProgress.status === 'completed' && 'Tamamlandı!'}
                    {importProgress.status === 'error' && 'Hata!'}
                  </span>
                  {importProgress.total > 0 && (
                    <span className="text-muted-foreground">{importProgress.processed || 0} / {importProgress.total}</span>
                  )}
                </div>
                <Progress value={importProgress.percent || 0} className="h-3" data-testid="import-progress-bar" />
                <p className="text-xs text-muted-foreground">{importProgress.message}</p>
                {importProgress.status === 'completed' && importProgress.total > 0 && (
                  <div className="flex gap-4 text-sm pt-1">
                    <span className="text-green-600 font-medium">Yeni: {importProgress.created || 0}</span>
                    <span className="text-blue-600 font-medium">Güncellenen: {importProgress.updated || 0}</span>
                    <span className="text-gray-600 font-medium">Toplam: {importProgress.total}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Nasıl Çalışır?</strong> <strong>XML:</strong> İlk yüklemede sistem XML&apos;inizi analiz eder ve alan eşleştirmesi önerir.
              <strong className="ml-1">Excel / CSV:</strong> Tablodaki sütun başlıkları için aynı eşleştirme mantığı kullanılır; kayıt <code className="text-xs bg-amber-100 px-1 rounded">tabular_mapping</code> olarak saklanır ve yalnızca bu sekmedeki yüklemelerde uygulanır.
              Değiştirmek için <strong className="text-blue-600 cursor-pointer" onClick={editCurrentMapping}>Düzenle</strong>.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerBulkUploadPage;
